import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Stack,
  Typography,
  alpha,
} from '@mui/material'
import MuiDrawer from '@mui/material/Drawer'
import { useTheme } from '@mui/material/styles'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'
import CloseIcon from '@mui/icons-material/Close'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import { FC, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { USER_ROLES } from '../all-types'
import { LW_POS_WHITE } from '../constants'
import { useAppSelector } from '../redux-store/hooks'
import { getSideBarByRole } from '../role-manager'

interface SideBarProps {
  open: boolean
  handleDrawerClose: () => void
  toggleTheme: () => void
}

// Oracle Redwood signature red — used sparingly as the active accent.
const ORACLE_RED = '#C74634'
const ORACLE_RED_DARK = '#A23528'

type NavItem = { url: string; icon: JSX.Element; name: string }

// Groups inspired by Oracle Redwood's sectioned navigation. Items not
// matched here are appended to "More" so the sidebar still works when the
// role-manager exposes additional routes later.
const NAV_GROUPS: { id: string; title: string; keys: string[] }[] = [
  {
    id: 'operations',
    title: 'Operations',
    keys: ['Dashboard', 'Order', 'OrderHistory', 'Kitchen', 'KitchenStats'],
  },
  {
    id: 'inventory',
    title: 'Inventory',
    keys: ['Inventory', 'Category'],
  },
  {
    id: 'administration',
    title: 'Administration',
    keys: ['Users', 'Layout', 'Settings'],
  },
  {
    id: 'tools',
    title: 'Tools',
    keys: ['AppDownload', 'Printer'],
  },
]

const SideBar: FC<SideBarProps> = ({ open, handleDrawerClose, toggleTheme }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const isDark = theme.palette.mode === 'dark'

  const client = useAppSelector((state) => state.clientInfo.data)
  const auth = useAppSelector((state) => state.authentication)
  const sidebarUrls = getSideBarByRole(auth.role as USER_ROLES) as Record<string, NavItem>

  // Solid Oracle-style canvas (no flashy gradient). Subtle warm cast on dark.
  const canvas = isDark ? '#0F1116' : '#F5F4F1'
  const surface = isDark ? '#171A21' : '#FFFFFF'
  const surfaceHover = isDark ? '#1E222B' : '#FAFAF7'
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,17,22,0.10)'
  const borderStrong = isDark ? 'rgba(255,255,255,0.16)' : 'rgba(15,17,22,0.18)'
  const textPrimary = isDark ? 'rgba(255,255,255,0.95)' : 'rgba(15,17,22,0.92)'
  const textMuted = isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,17,22,0.56)'
  const textFaint = isDark ? 'rgba(255,255,255,0.42)' : 'rgba(15,17,22,0.42)'
  const accent = ORACLE_RED
  const accentSoft = alpha(accent, isDark ? 0.18 : 0.10)
  const accentBorder = alpha(accent, isDark ? 0.45 : 0.35)
  const accentDeep = isDark ? accent : ORACLE_RED_DARK

  const handleNavigate = (url: string) => {
    navigate(url)
    handleDrawerClose()
  }

  // Build the grouped navigation, falling back to a "More" group for any
  // routes the role exposes that we didn't categorise above.
  const grouped = useMemo(() => {
    const allKeys = Object.keys(sidebarUrls)
    const usedKeys = new Set<string>()
    const result: { title: string; items: { key: string; item: NavItem }[] }[] = []

    NAV_GROUPS.forEach((group) => {
      const items = group.keys
        .filter((key) => sidebarUrls[key])
        .map((key) => {
          usedKeys.add(key)
          return { key, item: sidebarUrls[key] }
        })
      if (items.length) result.push({ title: group.title, items })
    })

    const leftover = allKeys
      .filter((key) => !usedKeys.has(key))
      .map((key) => ({ key, item: sidebarUrls[key] }))
    if (leftover.length) result.push({ title: 'More', items: leftover })

    return result
  }, [sidebarUrls])

  return (
    <MuiDrawer
      anchor="left"
      variant="temporary"
      open={open}
      onClose={handleDrawerClose}
      ModalProps={{
        keepMounted: true,
        sx: { zIndex: 13000 },
      }}
      BackdropProps={{
        sx: {
          background: alpha('#0A0B0F', 0.55),
          backdropFilter: 'blur(6px) saturate(140%)',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      }}
      PaperProps={{
        sx: {
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          border: 0,
          bgcolor: 'transparent',
          boxShadow: 'none',
          zIndex: 13001,
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          background: canvas,
          color: textPrimary,
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 1280,
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 3, md: 6, lg: 8 },
            py: { xs: 3, md: 5 },
          }}
        >
          {/* ── Top bar: brand + actions ── */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: { xs: 4, md: 5 } }}
          >
            <Stack direction="row" spacing={2.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                component="img"
                src={LW_POS_WHITE}
                alt="Bill Desk POS"
                sx={{
                  width: { xs: 124, md: 156 },
                  objectFit: 'contain',
                  filter: !isDark ? 'invert(1)' : 'none',
                  opacity: 0.94,
                }}
              />
              <Divider
                orientation="vertical"
                flexItem
                sx={{ height: 28, alignSelf: 'center', borderColor }}
              />
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    letterSpacing: '0.18em',
                    color: textFaint,
                    textTransform: 'uppercase',
                  }}
                >
                  Workspace
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    color: textPrimary,
                    letterSpacing: '-0.01em',
                    maxWidth: { xs: 200, md: 360 },
                  }}
                >
                  {client?.clientName || 'Bill Desk'}
                </Typography>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1.25}>
              <IconButton
                onClick={toggleTheme}
                aria-label="Toggle theme"
                sx={{
                  width: 44,
                  height: 44,
                  color: textPrimary,
                  bgcolor: surface,
                  border: `1px solid ${borderColor}`,
                  borderRadius: '12px',
                  transition: 'all 0.18s ease',
                  '&:hover': {
                    borderColor: borderStrong,
                    bgcolor: surfaceHover,
                  },
                }}
              >
                {isDark ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
              </IconButton>
              <IconButton
                onClick={handleDrawerClose}
                aria-label="Close menu"
                sx={{
                  width: 44,
                  height: 44,
                  color: textPrimary,
                  bgcolor: surface,
                  border: `1px solid ${borderColor}`,
                  borderRadius: '12px',
                  transition: 'all 0.18s ease',
                  '&:hover': {
                    borderColor: accentBorder,
                    color: accentDeep,
                    bgcolor: alpha(accent, isDark ? 0.12 : 0.06),
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          {/* ── Page heading ── */}
          <Stack spacing={0.5} sx={{ mb: { xs: 3, md: 4 } }}>
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.22em',
                color: accentDeep,
                textTransform: 'uppercase',
              }}
            >
              Navigation
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1.65rem', md: '2rem' },
                fontWeight: 700,
                color: textPrimary,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
              }}
            >
              What would you like to do?
            </Typography>
            <Typography sx={{ fontSize: '0.92rem', color: textMuted, fontWeight: 500 }}>
              Jump to any area of your workspace.
            </Typography>
          </Stack>

          {/* ── Grouped sections ── */}
          <Stack spacing={{ xs: 3.5, md: 4.5 }} sx={{ flex: 1 }}>
            {grouped.map((section) => (
              <Box key={section.title}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ mb: 1.75 }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 800,
                      letterSpacing: '0.2em',
                      color: textFaint,
                      textTransform: 'uppercase',
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Box sx={{ flex: 1, height: 1, bgcolor: borderColor }} />
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: textFaint,
                    }}
                  >
                    {section.items.length}
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      sm: 'repeat(3, minmax(0, 1fr))',
                      md: 'repeat(4, minmax(0, 1fr))',
                      lg: 'repeat(5, minmax(0, 1fr))',
                    },
                    gap: { xs: 1.25, md: 1.75 },
                  }}
                >
                  {section.items.map(({ key, item }) => {
                    const active = location.pathname === item.url
                    return (
                      <ButtonBase
                        key={key}
                        onClick={() => handleNavigate(item.url)}
                        sx={{
                          textAlign: 'left',
                          display: 'block',
                          width: '100%',
                          borderRadius: '14px',
                          bgcolor: active ? alpha(accent, isDark ? 0.13 : 0.06) : surface,
                          border: `1px solid ${active ? accentBorder : borderColor}`,
                          transition: 'all 0.18s ease',
                          position: 'relative',
                          overflow: 'hidden',
                          p: { xs: 1.75, md: 2.25 },
                          minHeight: { xs: 96, md: 112 },
                          '&:hover': {
                            borderColor: active ? accentBorder : borderStrong,
                            bgcolor: active
                              ? alpha(accent, isDark ? 0.18 : 0.09)
                              : surfaceHover,
                            transform: 'translateY(-1px)',
                            boxShadow: isDark
                              ? '0 6px 18px rgba(0,0,0,0.35)'
                              : '0 6px 18px rgba(15,17,22,0.06)',
                          },
                          '&:active': { transform: 'translateY(0)' },
                        }}
                      >
                        {active && (
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: 10,
                              bottom: 10,
                              width: 3,
                              borderRadius: 2,
                              bgcolor: accent,
                            }}
                          />
                        )}

                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ width: '100%', mb: 1.5 }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: active ? alpha(accent, 0.14) : accentSoft,
                              color: active ? accentDeep : textMuted,
                              border: `1px solid ${active ? accentBorder : borderColor}`,
                              transition: 'all 0.18s ease',
                            }}
                          >
                            {item.icon && (
                              <item.icon.type
                                {...item.icon.props}
                                sx={{ fontSize: 22 }}
                              />
                            )}
                          </Box>
                          <KeyboardArrowRightRoundedIcon
                            sx={{
                              fontSize: 18,
                              color: active ? accentDeep : textFaint,
                              transition: 'color 0.18s ease',
                            }}
                          />
                        </Stack>

                        <Typography
                          sx={{
                            fontSize: '0.98rem',
                            fontWeight: 700,
                            color: active ? accentDeep : textPrimary,
                            letterSpacing: '-0.005em',
                            lineHeight: 1.2,
                          }}
                          noWrap
                        >
                          {item.name}
                        </Typography>
                      </ButtonBase>
                    )
                  })}
                </Box>
              </Box>
            ))}
          </Stack>

          {/* ── Footer: signed-in user ── */}
          <Box
            sx={{
              mt: { xs: 4, md: 5 },
              p: { xs: 1.75, md: 2 },
              borderRadius: '14px',
              border: `1px solid ${borderColor}`,
              bgcolor: surface,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Avatar
              src={client?.clientLogo}
              sx={{
                width: 44,
                height: 44,
                bgcolor: accentSoft,
                color: accentDeep,
                fontWeight: 800,
                fontSize: '1.05rem',
                border: `1px solid ${accentBorder}`,
              }}
            >
              {auth?.username?.charAt(0).toUpperCase()}
            </Avatar>

            <Stack spacing={0.15} sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: textPrimary,
                  letterSpacing: '-0.005em',
                }}
              >
                {auth?.username || 'Operator'}
              </Typography>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Typography sx={{ fontSize: '0.78rem', color: textMuted, fontWeight: 600 }}>
                  {(auth?.role as string) || 'User'}
                </Typography>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: textFaint }} />
                <Typography sx={{ fontSize: '0.78rem', color: textFaint, fontWeight: 600 }}>
                  v2.26.2.18
                </Typography>
              </Stack>
            </Stack>

            <IconButton
              size="small"
              aria-label="Sign out"
              onClick={() => {
                localStorage.clear()
                window.location.assign('/')
              }}
              sx={{
                color: textMuted,
                border: `1px solid ${borderColor}`,
                borderRadius: '10px',
                width: 38,
                height: 38,
                transition: 'all 0.18s ease',
                '&:hover': {
                  color: accentDeep,
                  borderColor: accentBorder,
                  bgcolor: alpha(accent, isDark ? 0.12 : 0.06),
                },
              }}
            >
              <LogoutRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </MuiDrawer>
  )
}

export default SideBar
